<template>
  <div class="program-element">
    <el-row
      class="program-element__info"
      :gutter="20"
      @click.native="onProgramClick"
    >
      <el-col
        :span="2" 
        class="program-element__chevron"
      >
        <i :class="iconClassName" />
      </el-col>
      <el-col
        :span="5"
        class="program-element__title"
        v-text="program.name"
      />
      <el-col
        :span="3"
        class="program-element__type"
      >
        <app-i18n :code="`enumerators.programType.${program.type}`" />
      </el-col>
      <el-col
        :span="3"
        class="program-element__categories"
      >
        <app-i18n
          v-if="!program.categories || !program.categories.length"
          code="common.none"
        />
        <span
          v-else-if="program.categories.length === 1"
          v-text="program.categories[0].name"
        />
        <el-tooltip
          v-else
          effect="dark"
          :content="program.categories.map(c => c.name).join('\n')"
          placement="bottom"
        >
          <app-i18n code="entities.category.list.multipleCategories" />
        </el-tooltip>
      </el-col>
      <el-col
        :span="4"
        class="program-element__genders"
      >
        <template v-for="(gender, j) in program.genders">
        <app-i18n
          :key="`program-element__gender--${gender}`"
          :code="`enumerators.gender.${gender}`"
        />
        <span
          v-if="j < (program.genders.length - 1)"
          :key="`program-element__gender__separator--${gender}`"
          v-text="', '"
        />
        </template>
      </el-col>
      <el-col
        :span="4"
        class="program-element__locations"
      >
        <app-i18n
          v-if="!program.locations || !program.locations.length"
          code="common.none"
        />
        <span
          v-else-if="program.locations.length === 1"
          v-text="program.locations[0].name"
        />
        <el-tooltip
          v-else
          effect="dark"
          :content="program.locations.map(l => l.name).join('\n')"
          placement="bottom"
        >
          <app-i18n code="entities.program.list.multipleLocations" />
        </el-tooltip>
      </el-col>
      <el-col
        :span="5"
        class="program-element__buttons"
      >
        <el-button
          class="program-element__buttons__button"
          type="primary"
          :disabled="!canRegister"
          @click.native.stop.prevent="onApplyClick"
        >
          <app-i18n code="common.apply" />
        </el-button>
      </el-col>
    </el-row>

    <subprograms-list
      v-show="!isCollapsed"
      class="program-element__subprograms"
      :subprograms="program.subprograms"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import SubprogramsList from '@/components/SubprogramsList'

export default {
  name: 'program-element',
  props: {
    program: {
      type: Object,
      default: () => ({}),
    },
    isFirst: Boolean,
  },
  data() {
    return {
      isCollapsed: true,
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
    }),
    isProduction() {
      return process.env.VUE_APP_ENVIRONMENT === 'production'
    },
    slug() {
      return this.$route.params.slug
    },
    iconClassName() {
      if (this.isCollapsed) return 'icon-chevron-right'
      return 'icon-chevron-down'
    },
    canRegister() {
      return this.program.subprograms.some(subprogram => subprogram.status === 'regOpened')
    },
  },
  mounted() {
    this.$root.$on('programs.collapse.all', this.onCollapseAll)
    this.$root.$on('programs.uncollapse.first', this.onUncollapseFirst)
  },
  beforeDestroy() {
    this.$root.$off('programs.collapse.all', this.onCollapseAll)
    this.$root.$off('programs.uncollapse.first', this.onUncollapseFirst)
  },
  methods: {
    onProgramClick() {
      this.isCollapsed = !this.isCollapsed
      if (!this.isCollapsed) window.dispatchEvent(new Event('resize'))
    },
    onApplyClick() {
      if (!this.canRegister) return

      const applyFn = () => this.$router.push({
        name: 'program',
        params: {
          programId: this.program.id,
          slug: this.slug,
        }
      });

      if (!this.currentUser) {
        this.$root.$emit('modal.open.login', applyFn)
        return
      }

      applyFn()
    },
    onCollapseAll() {
      this.isCollapsed = true
    },
    onUncollapseFirst () {
      if (!this.isFirst) return
      this.isCollapsed = false
    },
  },
  components: {
    SubprogramsList
  },
}
</script>

<style lang="scss" scoped>
.program-element {
  display: grid;
  
  max-width: 100%;
  margin: 0 24px;

  &__info {
    position: relative;
    z-index: 1;

    height: 130px;
    box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
    background-color: #ffffff;
    cursor: pointer;

    display: flex;

    align-items: center;
    text-align: left;
    padding: 40px;
    margin: 8px 0 0 !important;

    .el-col:last-child {
      text-align: right;
    }

    @include mobile {
      height: 160px;
      margin-top: 8px;
      padding: 16px;
      display: grid;
      grid-template-areas:
          'chevron title'
          'chevron buttons';
      grid-template-columns: 0fr 1fr;
      justify-items: center;
      padding-right: 40px;
      // display: grid;
      // grid-template-columns: 30px auto auto 1fr 1fr;
      // grid-template-rows: auto 10px 10px auto;
      // grid-template-areas:
      //   "chevron title title title title"
      //   "chevron type genders genders genders"
      //   "chevron categories categories locations locations"
      //   "buttons buttons buttons buttons buttons";
      // grid-column-gap: 10px;
      // grid-row-gap: 16px;
      &::before,
      &::after {
        content: none;
      }
    }

    @include tablet {
      margin-top: 8px;
      // padding: 16px 24px;
      display: grid;
      grid-template-columns: 30px 25% 1fr 1fr auto;
      grid-template-areas:
        "chevron title type genders buttons"
        "chevron title categories locations buttons";
      grid-column-gap: 16px;
      grid-row-gap: 8px;

      &::before,
      &::after {
        content: none;
      }
    }
  }

  &__chevron,
  &__title,
  &__type,
  &__categories,
  &__genders,
  &__locations,
  &__buttons {
    font-size: 16px;

    @include mobile {
      width: auto;
      font-size: 12px;
    }

    @include tablet {
      width: auto;
      font-size: 14px;
    }
  }

  &__chevron {
    grid-area: chevron;
  }

  &__title {
    grid-area: title;

    font-size: 18px;
    font-weight: bold;
    color: #404040;

    @include mobile {
      font-size: 16px;
      text-align: center;
    }

    @include tablet {
      font-size: 16px;
    }
  }

  &__type {
    grid-area: type;
    @include mobile {
      display: none;
      font-size: 12px;
    }
  }

  &__categories {
    grid-area: categories;
    @include mobile {
      display: none;
      font-size: 12px;
    }
  }

  &__genders {
    grid-area: genders;
    @include mobile {
      display: none;
      font-size: 12px;
    }
  }

  &__locations {
    grid-area: locations;
    @include mobile {
      display: none;
      font-size: 12px;
    }
  }

  &__buttons {
    grid-area: buttons;
    @include mobile {
      justify-self: center;
    }
    &__button {
      min-width: 160px;
      align-self: flex-end;

      &.el-button {
        @include mobile {
          font-size: 12px;
          min-width: 80px;
        }

        @include tablet {
          font-size: 12px;
          min-width: 80px;
          padding: 10px 10px;
        }
      }
    }
  }

  // &__type {
  //   @include mobile {
  //     display: none;
  //   }

  //   @include tablet {
  //     display: none;
  //   }
  // }

  // &__categories {

  //   @include mobile {
  //     grid-area: categories;
  //     font-size: 14px;
  //     color: #969696;
  //   }

  //   @include tablet {
  //     grid-area: categories;
  //   }
  // }

  // &__genders {
  //   @include mobile {
  //     grid-area: genders;
  //     font-size: 12px;
  //     color: #969696;
  //   }

  //   @include tablet {
  //     grid-area: genders;
  //   }
  // }

  // &__locations {
  //   @include mobile {
  //     display: none;
  //   }

  //   @include tablet {
  //     display: none;
  //   }
  // }
}
</style>
